import React from 'react'
import { DayPicker } from 'react-day-picker'
import PropTypes from 'prop-types'
import pageStyles from '../../../../css/devicePage.module.css'

/**
 * Reusable DateRangePicker component
 * Can be used with or without the useDateRangePicker hook
 *
 * @param {Object} props
 * @param {moment.Moment} props.dateBegin - Start date
 * @param {moment.Moment} props.dateEnd - End date
 * @param {string} props.dateType - 'from', 'to', or undefined
 * @param {boolean} props.dayPickerIsVisible - Whether the picker modal is visible
 * @param {Function} props.onDateBeginChange - Callback when dateBegin changes
 * @param {Function} props.onDateEndChange - Callback when dateEnd changes
 * @param {Function} props.onClose - Callback to close the picker
 * @param {Function} props.onEditFrom - Callback when "from" button is clicked
 * @param {Function} props.onEditTo - Callback when "to" button is clicked
 * @param {Function} props.onDayClick - Callback when a day is selected
 * @param {Function} props.onResetToLast31Days - Callback for reset button
 * @param {string} props.buttonClassName - Additional CSS class for date buttons
 * @param {Object} props.buttonStyle - Additional styles for date buttons
 * @param {string} props.layout - 'inline' (buttons in layout) or 'modal' (modal only)
 */
const DateRangePicker = ({
  dateBegin,
  dateEnd,
  dateType,
  dayPickerIsVisible,
  draftRange,
  onDateBeginChange,
  onDateEndChange,
  onClose,
  onEditRange,
  onEditFrom,
  onEditTo,
  onDayClick,
  onApplyRange,
  onResetToLast31Days,
  buttonClassName,
  buttonStyle,
  layout = 'modal', // 'inline', 'modal', or 'both'
}) => {
  const from = dateBegin ? new Date(dateBegin.toDate()) : new Date()
  const to = dateEnd ? new Date(dateEnd.toDate()) : new Date()

  const handleOpen = React.useCallback(() => {
    if (onEditRange) {
      onEditRange()
    } else if (onEditFrom) {
      onEditFrom()
    } else if (onEditTo) {
      onEditTo()
    }
  }, [onEditFrom, onEditRange, onEditTo])

  const selectedRange = draftRange || { from, to }
  const hasCompleteSelection = Boolean(selectedRange?.from && selectedRange?.to)
  const rangeLabel = `${dateBegin ? dateBegin.format('ll') : ''} → ${dateEnd ? dateEnd.format('ll') : ''}`

  const dateButtons = (
    <span style={{ flex: 1, marginLeft: '35px', ...buttonStyle }}>
      <button
        type='button'
        onClick={handleOpen}
        className={
          dayPickerIsVisible
            ? pageStyles.dateButtonStyleSelected
            : pageStyles.dateButtonStyle || buttonClassName
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          justifyContent: 'center',
          border: 'none',
          borderBottom: 'none',
          boxShadow: 'none',
          outline: 'none',
          background: 'transparent',
        }}
      >
        <span>{rangeLabel}</span>
      </button>
    </span>
  )

  const modal = dayPickerIsVisible && (
    <div style={{
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: '999',
    }}
    onClick={(e) => {
      // Close when clicking outside the modal content
      if (e.target === e.currentTarget) onClose()
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          marginBottom: '12px',
          padding: '8px 12px',
          backgroundColor: '#f0f8ff',
          borderRadius: '6px',
          fontSize: '14px',
          color: '#4a90e2',
          textAlign: 'center',
        }}
        >
          Select a date range (maximum 31 days)
        </div>
        {onResetToLast31Days && (
          <div style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'center',
          }}
          >
            <button
              type='button'
              onClick={onResetToLast31Days}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4a90e2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#51A0FA'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#4a90e2'
              }}
            >
              Reset to Last 31 Days
            </button>
          </div>
        )}
        <DayPicker
          mode='range'
          className='Selectable'
          disabled={{ after: new Date() }}
          defaultMonth={selectedRange?.from || from}
          selected={selectedRange}
          onSelect={onDayClick}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '16px',
          gap: '12px',
        }}
        >
          <button
            type='button'
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={onApplyRange}
            disabled={!hasCompleteSelection}
            style={{
              padding: '8px 16px',
              backgroundColor: hasCompleteSelection ? '#4a90e2' : '#b8d4f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: hasCompleteSelection ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              if (hasCompleteSelection) e.target.style.backgroundColor = '#51A0FA'
            }}
            onMouseLeave={(e) => {
              if (hasCompleteSelection) e.target.style.backgroundColor = '#4a90e2'
            }}
          >
            OK
          </button>
        </div>
        <button
          type='button'
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#666',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f0f0f0'
            e.target.style.color = '#000'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent'
            e.target.style.color = '#666'
          }}
        >
          ×
        </button>
      </div>
    </div>
  )

  if (layout === 'inline') {
    return dateButtons
  }

  if (layout === 'modal') {
    return modal
  }

  // layout === 'both'
  return (
    <>
      {dateButtons}
      {modal}
    </>
  )
}

DateRangePicker.propTypes = {
  dateBegin: PropTypes.object, // moment object
  dateEnd: PropTypes.object, // moment object
  dateType: PropTypes.string,
  dayPickerIsVisible: PropTypes.bool,
  draftRange: PropTypes.object,
  onDateBeginChange: PropTypes.func,
  onDateEndChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onEditRange: PropTypes.func,
  onEditFrom: PropTypes.func,
  onEditTo: PropTypes.func,
  onDayClick: PropTypes.func.isRequired,
  onApplyRange: PropTypes.func,
  onResetToLast31Days: PropTypes.func,
  buttonClassName: PropTypes.string,
  buttonStyle: PropTypes.object,
  layout: PropTypes.oneOf(['inline', 'modal', 'both']),
}

export default DateRangePicker
